import React, { useState, useEffect } from 'react';
import { FaGithub, FaLinkedin, FaPhone, FaWhatsapp, FaTiktok } from 'react-icons/fa';
import { SiGmail, SiX } from 'react-icons/si';
import { motion } from 'framer-motion';

function Contact() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const phoneNumber = '+2347019040834';
  const messages = 'Hello, I would like to know more!';
  const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(messages)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('');

    try {
      const response = await fetch('https://formspree.io/f/xpwzzopy', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, message }),
      });

      if (response.ok) {
        setStatus('Thanks for reaching out! I will get back to you shortly. 😁👍');
        setName('');
        setEmail('');
        setMessage('');
      } else {
        setStatus('There was an error. Please try again or message me directly.');
      }
    } catch (err) {
      setStatus('Could not send message. Please check your internet connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen pt-28 pb-16 overflow-hidden bg-grid-pattern">
      <div className="glow-spot top-1/3 left-1/3 -translate-x-1/2"></div>

      <div className="max-w-6xl mx-auto w-full px-5 md:px-10 lg:px-24 relative z-10 space-y-12 text-start">
        
        {/* Title */}
        <div className="space-y-4">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Let's build something.
          </h1>
          <div className="h-1.5 w-16 bg-teal-500 rounded-full"></div>
          <p className="text-slate-600 dark:text-slate-400 text-sm md:text-base max-w-lg">
            Have a project in mind, want to collaborate, or just talk tech? Drop a message below or reach out directly.
          </p>
        </div>

        {/* Dual Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Sidebar Connection Card */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-4 p-6 rounded-2xl glass space-y-6"
          >
            <h2 className="text-lg font-bold text-slate-900 dark:text-white pb-2 border-b border-slate-200/50 dark:border-slate-800/50">
              Direct Channels
            </h2>

            <div className="space-y-4 text-xs md:text-sm text-slate-700 dark:text-slate-300">
              
              <a href="mailto:amiolademilade@gmail.com" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-100/50 dark:hover:bg-slate-800/40 border border-transparent hover:border-slate-200/50 dark:hover:border-slate-800/50 transition-all">
                <SiGmail className="text-red-500" size={16} />
                <div className="flex flex-col">
                  <span className="text-[9px] font-bold text-slate-400 uppercase">Email</span>
                  <span className="font-semibold">amiolademilade@gmail.com</span>
                </div>
              </a>

              <a href="tel:07019040834" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-100/50 dark:hover:bg-slate-800/40 border border-transparent hover:border-slate-200/50 dark:hover:border-slate-800/50 transition-all">
                <FaPhone className="text-teal-600 dark:text-teal-400 rotate-90" size={16} />
                <div className="flex flex-col">
                  <span className="text-[9px] font-bold text-slate-400 uppercase">Call / SMS</span>
                  <span className="font-semibold">+234 70 1904 0834</span>
                </div>
              </a>

              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-100/50 dark:hover:bg-slate-800/40 border border-transparent hover:border-slate-200/50 dark:hover:border-slate-800/50 transition-all">
                <FaWhatsapp className="text-green-500" size={16} />
                <div className="flex flex-col">
                  <span className="text-[9px] font-bold text-slate-400 uppercase">WhatsApp</span>
                  <span className="font-semibold">Chat instantly</span>
                </div>
              </a>

            </div>

            {/* Social handles list */}
            <div className="pt-4 border-t border-slate-200/50 dark:border-slate-800/50 space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 code-font">Socials</h3>
              <div className="flex gap-4">
                <a href="https://github.com/feranmiba" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full glass hover:text-teal-600 dark:hover:text-teal-400 transition-colors" aria-label="GitHub">
                  <FaGithub size={16} />
                </a>
                <a href="https://www.linkedin.com/in/amiola-demilade-5a46801a8/" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full glass hover:text-teal-600 dark:hover:text-teal-400 transition-colors" aria-label="LinkedIn">
                  <FaLinkedin size={16} />
                </a>
                <a href="https://x.com/emmanuel_amiola" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full glass hover:text-teal-600 dark:hover:text-teal-400 transition-colors" aria-label="X">
                  <SiX size={14} />
                </a>
                <a href="https://www.tiktok.com/@amidev2" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full glass hover:text-teal-600 dark:hover:text-teal-400 transition-colors" aria-label="TikTok">
                  <FaTiktok size={15} />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Formspree message sender */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-8 p-6 md:p-8 rounded-2xl glass"
          >
            <form onSubmit={handleSubmit} className="space-y-6 text-xs md:text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Name */}
                <div className="space-y-2">
                  <label htmlFor="name" className="font-bold text-slate-600 dark:text-slate-400 uppercase text-[10px] tracking-wider code-font">Your Name</label>
                  <input 
                    type="text" 
                    id="name"
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    required 
                    placeholder="John Doe"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 outline-none text-slate-800 dark:text-white focus:border-teal-500 transition-colors"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label htmlFor="email" className="font-bold text-slate-600 dark:text-slate-400 uppercase text-[10px] tracking-wider code-font">Your Email</label>
                  <input 
                    type="email" 
                    id="email"
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    required 
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 outline-none text-slate-800 dark:text-white focus:border-teal-500 transition-colors"
                  />
                </div>

              </div>

              {/* Message */}
              <div className="space-y-2">
                <label htmlFor="message" className="font-bold text-slate-600 dark:text-slate-400 uppercase text-[10px] tracking-wider code-font">Your Message</label>
                <textarea 
                  id="message"
                  value={message} 
                  onChange={(e) => setMessage(e.target.value)} 
                  required 
                  rows={5} 
                  placeholder="Hey, let's work on..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 outline-none text-slate-800 dark:text-white focus:border-teal-500 transition-colors resize-none"
                />
              </div>

              {/* Action Button */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex-1 max-w-md">
                  {status && (
                    <span className={`font-semibold text-xs ${status.includes('Thanks') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                      {status}
                    </span>
                  )}
                </div>
                
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white dark:bg-teal-600 dark:hover:bg-teal-500 dark:text-white rounded-full font-bold tracking-wide transition-colors cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </div>

            </form>
          </motion.div>

        </div>
      </div>
    </div>
  );
}

export default Contact;
